/**
 * A small, surface-agnostic owner for MapLibre style generations.
 *
 * MapLibre drops runtime sources, layers and images whenever setStyle runs.
 * A style-load event has no generation information of its own, so consumers
 * must retain the generation that requested it and discard late async work.
 */
export function scheduleAssetReconciliation({
  prepareAssets,
  revision,
  isCurrent,
  requestReconcile,
  onError,
}) {
  void Promise.resolve()
    .then(prepareAssets)
    .then((didChange) => {
      if (didChange && isCurrent(revision)) requestReconcile?.();
    })
    .catch((error) => {
      if (isCurrent(revision)) onError?.(error, revision);
    });
}

export function createMapStyleLifecycle({ map, onReady, onError } = {}) {
  let styleRevision = 0;
  let readyRevision = 0;
  let loadedRevision = 0;
  let disposed = false;
  let pendingRevision = 0;
  let pendingReconcilePromise = null;
  let requestedRevision = 0;

  const reconcile = () => {
    const revision = pendingRevision;
    if (!revision || loadedRevision !== revision || !isCurrent(revision)) {
      return Promise.resolve();
    }

    // A data/selection update may arrive while assets are being prepared for
    // this style. Keep one serial worker and coalesce it into a follow-up pass
    // rather than dropping that update or allowing concurrent MapLibre writes.
    requestedRevision = revision;
    if (pendingReconcilePromise) return pendingReconcilePromise;

    pendingReconcilePromise = (async () => {
      while (requestedRevision && !disposed) {
        const nextRevision = requestedRevision;
        requestedRevision = 0;
        if (!isCurrent(nextRevision)) continue;
        try {
          await onReady?.(nextRevision, isCurrent);
          if (isCurrent(nextRevision)) readyRevision = nextRevision;
        } catch (error) {
          if (isCurrent(nextRevision)) onError?.(error, nextRevision);
        }
      }
    })().finally(() => {
      pendingReconcilePromise = null;
    });
    return pendingReconcilePromise;
  };

  const styleReadyListener = () => {
    if (readyRevision === styleRevision) return;
    loadedRevision = styleRevision;
    void reconcile();
  };

  function isCurrent(revision) {
    return !disposed && revision === styleRevision;
  }

  map?.on?.("style.load", styleReadyListener);
  map?.on?.("idle", styleReadyListener);

  return {
    get styleRevision() {
      return styleRevision;
    },
    get readyRevision() {
      return readyRevision;
    },
    beginStyle(style) {
      styleRevision += 1;
      pendingRevision = styleRevision;
      readyRevision = 0;
      loadedRevision = 0;
      // A URL style diff leaves the old style loaded while fetching the new
      // document, and successful diffs do not emit style.load. Full replacement
      // detaches the old style immediately, giving each revision its own load
      // boundary before runtime sources and images are installed.
      if (style !== undefined) map?.setStyle?.(style, { diff: false });
      return styleRevision;
    },
    isCurrent,
    reconcile,
    dispose() {
      disposed = true;
      pendingRevision = 0;
      requestedRevision = 0;
      map?.off?.("style.load", styleReadyListener);
      map?.off?.("idle", styleReadyListener);
    },
  };
}
